import React from 'react'
import { Checkbox, Form, Radio, Select, Spin, Switch } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { getMemberLevelList, loadMemberSettingConfig, saveMemberSettingConfig } from '../api'
import {
    getMemberPriceSvipSelectionError,
    isSvipMemberPriceAvailable,
    MemberPriceLevelValue,
} from './member-price-utils'
import SettingPage, {
    MONTH_OPTIONS,
    SCENE_OPTIONS,
    SettingActions,
    SettingGrid,
    SettingPanel,
    SettingPreview,
    WEEK_OPTIONS,
    statusToBool,
} from './setting-page'
import { useMemberSettingAccess } from './setting-access'

interface MemberPriceForm {
    enable: boolean
    frequency: number
    weekdays?: number[]
    monthdays?: number[]
    scenes?: number[]
    threshold: number
    member_level_id: MemberPriceLevelValue
}

const MemberPrice: React.FC = () => {
    const [form] = Form.useForm<MemberPriceForm>()
    const access = useMemberSettingAccess()
    const levelReq = useRequest(getMemberLevelList, {
        onError: e => $msg('error', e.message || '获取会员等级失败'),
    })
    const svipReq = useRequest(() => loadMemberSettingConfig('/membership/getsvipconfigadmin'), {
        onError: e => $msg('error', e.message || '获取付费会员设置失败'),
    })
    const svipEnabled = isSvipMemberPriceAvailable(svipReq.data)
    const loadReq = useRequest(() => loadMemberSettingConfig('/activity/getmemberpriceactivity'), {
        onSuccess: res => {
            form.setFieldsValue({
                enable: statusToBool(res?.status),
                frequency: res?.frequency === 0 ? 1 : res?.frequency || 1,
                weekdays: res?.weekdays || [],
                monthdays: res?.monthdays || [],
                scenes: res?.scenes || [],
                threshold: res?.threshold ?? 0,
                member_level_id: res?.suit_level?.name
                    ? res.suit_level.id === null
                        ? 'svip'
                        : res.suit_level.id
                    : 'none',
            })
        },
        onError: e => $msg('error', e.message || '获取商品会员价失败'),
    })

    const saveReq = useRequest(
        async (values: MemberPriceForm) => {
            if (!values.enable) {
                await saveMemberSettingConfig('/activity/updatememberpriceactivity', {
                    enable: false,
                })

                return
            }
            const selectedLevel = (levelReq.data || []).find((item: any) => item.id === values.member_level_id)
            await saveMemberSettingConfig('/activity/updatememberpriceactivity', {
                enable: values.enable,
                frequency: values.frequency,
                weekdays: values.frequency === 1 ? values.weekdays || [] : null,
                monthdays: values.frequency === 2 ? values.monthdays || [] : null,
                scenes: values.scenes || [],
                threshold: values.threshold,
                member_level_id:
                    values.member_level_id === 'none'
                        ? 0
                        : values.member_level_id === 'svip'
                        ? null
                        : values.member_level_id,
                level:
                    values.member_level_id === 'none'
                        ? -1
                        : values.member_level_id === 'svip'
                        ? null
                        : selectedLevel?.level,
            })
        },
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => $msg('error', e.message || '保存商品会员价失败'),
        },
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        if (values.enable) {
            const activeDays = values.frequency === 2 ? values.monthdays || [] : values.weekdays || []
            if (!activeDays.length) {
                $msg('warning', '请选择商品会员价生效日期')

                return
            }
            if (!(values.scenes || []).length) {
                $msg('warning', '请选择商品会员价适用场景')

                return
            }
            if (values.member_level_id === 'none') {
                $msg('warning', '请选择商品会员价适用等级')

                return
            }
            const svipSelectionError = getMemberPriceSvipSelectionError(values.member_level_id, svipEnabled)
            if (svipSelectionError) {
                $msg('warning', svipSelectionError)

                return
            }
        }
        saveReq.run(values)
    }

    return (
        <SettingPage title="商品会员价" readOnly={access.readOnly}>
            <Spin spinning={loadReq.loading || levelReq.loading || svipReq.loading}>
                <Form form={form} layout="vertical">
                    <SettingGrid>
                        <SettingPanel title="生效规则" desc="控制商品会员价在收银台和商城的适用范围。" wide>
                            <Form.Item noStyle shouldUpdate>
                                {() => {
                                    const selectedLevel = (levelReq.data || []).find(
                                        (level: any) => level.id === form.getFieldValue('member_level_id'),
                                    )
                                    const levelName =
                                        form.getFieldValue('member_level_id') === 'svip'
                                            ? '付费会员'
                                            : selectedLevel?.name || '--'

                                    return (
                                        <SettingPreview
                                            items={[
                                                {
                                                    label: '活动状态',
                                                    value: form.getFieldValue('enable') ? '已开启' : '已关闭',
                                                },
                                                { label: '适用等级', value: levelName },
                                                {
                                                    label: '适用场景',
                                                    value:
                                                        (form.getFieldValue('scenes') || [])
                                                            .map((scene: number) => (scene === 0 ? '收银台' : '商城'))
                                                            .join('、') || '--',
                                                },
                                            ]}
                                        />
                                    )
                                }}
                            </Form.Item>
                            <Form.Item name="enable" label="活动状态" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="frequency" label="生效周期">
                                <Radio.Group>
                                    <Radio value={1}>每周</Radio>
                                    <Radio value={2}>每月</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate={(prev, next) => prev.frequency !== next.frequency}>
                                {() =>
                                    form.getFieldValue('frequency') === 2 ? (
                                        <Form.Item name="monthdays" label="每月生效日期">
                                            <Checkbox.Group options={MONTH_OPTIONS} />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item name="weekdays" label="每周生效日期">
                                            <Checkbox.Group options={WEEK_OPTIONS} />
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>
                            <Form.Item name="scenes" label="适用场景">
                                <Checkbox.Group options={SCENE_OPTIONS} />
                            </Form.Item>
                            <Form.Item name="threshold" label="支付限制">
                                <Radio.Group>
                                    <Radio value={0}>不限支付方式</Radio>
                                    <Radio value={1}>订单结算使用余额</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item name="member_level_id" label="适用等级">
                                <Select className="member-config-page__select">
                                    <Select.Option value="none">暂不分配给会员等级</Select.Option>
                                    <Select.Option value="svip" disabled={!svipEnabled}>
                                        {svipEnabled ? '付费会员' : '付费会员（需先开启）'}
                                    </Select.Option>
                                    {(levelReq.data || []).map((level: any) => (
                                        <Select.Option key={level.id} value={level.id}>
                                            {level.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <SettingActions>
                                <Auth perssion={20} onClick={onSubmit}>
                                    <Button types="default" loading={saveReq.loading} disabled={saveReq.loading}>
                                        保存
                                    </Button>
                                </Auth>
                            </SettingActions>
                        </SettingPanel>
                    </SettingGrid>
                </Form>
            </Spin>
        </SettingPage>
    )
}

export default MemberPrice
